import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { phraseService } from '../services/phraseService';
import { TopBar } from '../components/common/TopBar';
import { Button } from '../components/common/Button';
import { LoginButton } from '../components/common/LoginButton';
import './Study.css';

const StudyFlashcards = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [cards, setCards] = useState([]);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState({ remembered: {}, forgotten: {} });
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    const load = async () => {
      const [phrases, prog] = await Promise.all([
        phraseService.getUserPhrases(currentUser.uid),
        phraseService.getStudyProgress(currentUser.uid),
      ]);
      // Prioritize forgotten words
      const forgotten = new Set(Object.keys(prog.forgotten || {}));
      const sorted = [
        ...phrases.filter((p) => forgotten.has(p.word)),
        ...phrases.filter((p) => !forgotten.has(p.word)),
      ];
      setCards(sorted);
      setProgress(prog);
      setLoading(false);
    };
    load();
  }, [currentUser]);

  const current = cards[index];

  const handleFeedback = async (remembered) => {
    if (currentUser && current) {
      await phraseService.saveFlashcardResult(currentUser.uid, current.word, remembered);
    }
    setFlipped(false);
    const next = index + 1;
    if (next >= cards.length) {
      setDone(true);
      return;
    }
    setTimeout(() => setIndex(next), 150);
  };

  // ── No account ────────────────────────────────────
  if (!currentUser) {
    return (
      <div className="study-page">
        <TopBar title="Flashcard Guesser" onBack={() => navigate('/study')} />
        <div className="study-body study-empty">
          <span className="study-empty__icon">🔐</span>
          <p>Devi effettuare il login per usare le flashcard.</p>
          <LoginButton className="study-next-btn">Accedi</LoginButton>
        </div>
      </div>
    );
  }

  // ── Loading ────────────────────────────────────────
  if (loading) {
    return (
      <div className="study-page">
        <TopBar title="Flashcard Guesser" onBack={() => navigate('/study')} />
        <div className="study-body">
          <p style={{ color: 'var(--color-text-muted)' }}>Caricamento…</p>
        </div>
      </div>
    );
  }

  // ── No phrases ─────────────────────────────────────
  if (cards.length === 0) {
    return (
      <div className="study-page">
        <TopBar title="Flashcard Guesser" onBack={() => navigate('/study')} />
        <div className="study-body study-empty">
          <span className="study-empty__icon">📭</span>
          <p>Non hai ancora nessuna frase salvata.</p>
          <p style={{ color: 'var(--color-text-dim)', fontSize: '0.85rem' }}>
            Vai in "Gestisci Frasi" per aggiungerne.
          </p>
          <Button onClick={() => navigate('/phrases')}>Gestisci Frasi</Button>
        </div>
      </div>
    );
  }

  // ── Done ──────────────────────────────────────────
  if (done) {
    const rem = Object.keys(progress.remembered || {}).length;
    const forg = Object.keys(progress.forgotten || {}).length;
    return (
      <div className="study-page">
        <TopBar title="Flashcard Guesser" onBack={() => navigate('/study')} />
        <div
          className="study-body u-scale-in"
          style={{ textAlign: 'center', gap: 'var(--spacing-xl)' }}
        >
          <span style={{ fontSize: '4rem' }}>🎉</span>
          <h2 style={{ fontSize: '2rem', fontWeight: 900, color: 'var(--color-text-main)' }}>
            Sessione completata!
          </h2>
          <div style={{ display: 'flex', gap: 'var(--spacing-lg)' }}>
            <div className="end-stat">
              <span className="end-stat__v">{cards.length}</span>
              <span className="end-stat__l">Carte</span>
            </div>
            <div className="end-stat">
              <span className="end-stat__v" style={{ color: 'var(--color-success)' }}>
                {rem}
              </span>
              <span className="end-stat__l">Ricordate</span>
            </div>
            <div className="end-stat">
              <span className="end-stat__v" style={{ color: 'var(--color-error)' }}>
                {forg}
              </span>
              <span className="end-stat__l">Dimenticate</span>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--spacing-md)',
              alignItems: 'center',
            }}
          >
            <button
              className="start-btn"
              onClick={() => {
                setIndex(0);
                setFlipped(false);
                setDone(false);
              }}
            >
              Ricomincia
            </button>
            <button className="back-link" onClick={() => navigate('/study')}>
              ← Studio
            </button>
          </div>
        </div>
      </div>
    );
  }

  // removed unused var
  const total = cards.length;

  return (
    <div className="study-page">
      <TopBar
        title="Flashcard Guesser"
        onBack={() => navigate('/study')}
        actions={
          <span className="study-counter">
            {index + 1}/{total}
          </span>
        }
      />

      {/* Progress bar */}
      <div className="tg-progress">
        <div className="tg-progress__fill" style={{ width: `${(index / total) * 100}%` }} />
      </div>

      <div className="study-body">
        {/* Flip card */}
        <div className="card-area" onClick={() => setFlipped((f) => !f)}>
          <div className={`flashcard ${flipped ? 'flipped' : ''}`}>
            <div className="card-front">
              <p className="card-label">Frase</p>
              <p className="card-phrase">"{current.phrase}"</p>
              <p className="card-tap-hint">Tocca per vedere la parola</p>
            </div>
            <div className="card-back">
              <p className="card-label">Parola</p>
              <p className="card-word">{current.word}</p>
            </div>
          </div>
        </div>

        {/* Feedback buttons — shown only when flipped */}
        {flipped ? (
          <div className="flashcard-feedback u-slide-up">
            <button
              className="flashcard-btn flashcard-btn--forget"
              onClick={() => handleFeedback(false)}
              id="btn-forget"
            >
              ✗ Non ricordo
            </button>
            <button
              className="flashcard-btn flashcard-btn--remember"
              onClick={() => handleFeedback(true)}
              id="btn-remember"
            >
              ✓ Ricordo!
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default StudyFlashcards;
